import type { SearchGroup, SearchResult } from '../../lib/search-types';
import { TopBarSearchResultItem } from './top-bar-search-result-item';

const DROPDOWN_STYLE = {
  borderColor: 'rgba(255,255,255,0.12)',
  background: 'linear-gradient(180deg, #1c1d26 0%, #16171e 100%)',
  boxShadow: '0 24px 48px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)',
} as const;

interface TopBarSearchDropdownProps {
  results: SearchResult[];
  activeIndex: number;
  isSearching: boolean;
  isConfigured: boolean;
  onSelect: (result: SearchResult) => void;
  onHover: (index: number) => void;
}

function groupResults(results: SearchResult[]): [SearchGroup, SearchResult[]][] {
  const map = new Map<SearchGroup, SearchResult[]>();
  for (const r of results) {
    const group = map.get(r.group) ?? [];
    group.push(r);
    map.set(r.group, group);
  }
  return Array.from(map.entries());
}

function EmptyState({
  isSearching,
  isConfigured,
}: {
  isSearching: boolean;
  isConfigured: boolean;
}) {
  return (
    <div
      className="absolute left-1/2 top-full z-40 mt-2 w-[min(36rem,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden rounded-xl border py-8 text-center"
      style={DROPDOWN_STYLE}
      role="listbox"
      aria-label="Søkeresultater"
    >
      <p className="text-sm text-text-secondary">{isSearching ? 'Søker…' : 'Ingen treff'}</p>
      <p className="mt-1 text-[11px] text-text-tertiary">
        {isConfigured
          ? 'Prøv et annet ord fra tittel, innhold, avsender eller vedlegg'
          : 'Søk er ikke konfigurert · viser saker'}
      </p>
    </div>
  );
}

export function TopBarSearchDropdown({
  results,
  activeIndex,
  isSearching,
  isConfigured,
  onSelect,
  onHover,
}: TopBarSearchDropdownProps) {
  if (results.length === 0) {
    return <EmptyState isSearching={isSearching} isConfigured={isConfigured} />;
  }

  const groups = groupResults(results);
  let flatIndex = 0;

  return (
    <div
      className="absolute left-1/2 top-full z-40 mt-2 max-h-[min(28rem,70vh)] w-[min(36rem,calc(100vw-2rem))] -translate-x-1/2 overflow-y-auto rounded-xl border py-2"
      style={DROPDOWN_STYLE}
      role="listbox"
      aria-label="Søkeresultater"
    >
      {groups.map(([group, items]) => (
        <div key={group} className="pb-1">
          <div className="flex items-baseline justify-between px-3.5 pb-1 pt-2">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-tertiary">
              {group}
            </span>
            <span className="text-[10px] tabular-nums text-text-tertiary">{items.length}</span>
          </div>
          {items.map((item) => {
            const idx = flatIndex++;
            return (
              <TopBarSearchResultItem
                key={`${item.group}-${item.id}`}
                item={item}
                isActive={idx === activeIndex}
                onSelect={onSelect}
                onHover={() => onHover(idx)}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}
